import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";

const MultipleLocationModal = ({
  isOpen,
  onClose,
  date,
  toDate,
  onDataSubmit,
  dataUrl,
  onPendingChange,
}) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLocationIDs, setSelectedLocationIDs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pending, setPending] = useState(false);
  const adminInfo = JSON.parse(sessionStorage.getItem("adminInfo") || "{}");
  const token = sessionStorage.getItem("token");
  const [apiError, setApiError] = useState("");
  const [selectedAllLocation, setSelectedAllLocation] = useState([]);
  const [selectedLocationDet, setSelectedLocationDet] = useState([]);

  const toastStyle = {
    fontFamily: "QSemiBold",
    fontSize: "14px",
    fontWeight: 500,
  };

  const fetchUserLocations = async () => {
    if (!token) throw new Error("Missing token");

    const adminId = adminInfo?.id;
    if (!adminId) throw new Error("Missing admin ID");

    const endpoint = `${process.env.REACT_APP_BACKEND_API_URL}/user/selectedLocation/${adminId}`;

    const { data } = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    return data;
  };

  const {
    data = {},
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["userLocations"],
    queryFn: fetchUserLocations,
    enabled: isOpen,
  });

  const locations = data?.data || [];
  const isMutipleLocationIDs = adminInfo?.multiple_location;

  // ── Pre-populate from sessionStorage when modal opens and data loads ──────
  useEffect(() => {
    if (!isOpen || !locations?.selected_location?.length) return;

    const storedLocationId = (() => {
      try {
        const raw = sessionStorage.getItem("selectedLocation");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();

    const storedSubIds = (() => {
      try {
        const raw = sessionStorage.getItem("locations");
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    })();

    if (!storedLocationId) return;

    // Find the matching location entry
    const matched = locations.selected_location.find(
      (loc) => loc.locationEndPoint._id === storedLocationId,
    );

    if (matched) {
      setSelectedLocation(matched.locationEndPoint);
      // Use stored sub-location IDs if available, otherwise default to all
      const fallback = (matched.locationID || []).map((l) => l.id);
      setSelectedLocationIDs(
        storedSubIds.length > 0 ? storedSubIds : fallback,
      );
      setSelectedAllLocation(matched.locationID || []);
    }
  }, [isOpen, locations]);

  // ── Reset when modal closes ───────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setSelectedLocation(null);
      setSelectedLocationIDs([]);
    }
  }, [isOpen]);

  const handleCheckboxChange = (id) => {
    setSelectedLocationIDs((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    const stored = localStorage.getItem("selectedAllLocation");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedAllLocation(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedAllLocation:", e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchDataOnFromDateChange = async () => {
      if (isOpen || !date || isMutipleLocationIDs.length === 0) return;
      setPending(true);
      onPendingChange?.(true);
      try {
        const token = sessionStorage.getItem("token");
        const selectedLocationID = JSON.parse(
          sessionStorage.getItem("selectedLocation"),
        );
        const selectedLocation = JSON.parse(
          sessionStorage.getItem("locations") || "[]",
        );

        const isoDate = new Date(date).toISOString();
        const isoToDate = new Date(toDate).toISOString();

        const requestBody = {
          id: selectedLocationID || "",
          dataurl: `/api/${dataUrl}`,
          subdata: {
            Loations: selectedLocation || [],
            date: isoDate,
            toDate: isoToDate,
          },
        };

        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/clients/multipleLocations-summarydetails`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        const summary = data?.result?.summary || data?.result || [];
        const locationWise = data?.result?.locationWise || [];

        if (onDataSubmit && selectedAllLocation.length > 0) {
          onDataSubmit({
            summary,
            locationWise,
            selectedLocationDet,
            selectedAllLocation,
          });
        }
      } catch (error) {
        console.error("Error fetching data on date change:", error);
      } finally {
        setPending(false);
        onPendingChange?.(false);
      }
    };

    fetchDataOnFromDateChange();
  }, [date, isOpen]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setApiError("");
    try {
      const token = sessionStorage.getItem("token");

      if (
        !selectedLocation ||
        !token ||
        !date ||
        selectedLocationIDs.length === 0
      ) {
        throw new Error("Missing required data");
      }

      const isoDate = new Date(date).toISOString();
      const isoToDate = new Date(toDate).toISOString();

      const requestBody = {
        id: selectedLocation._id,
        dataurl: `/api/${dataUrl}`,
        subdata: {
          Loations: selectedLocationIDs,
          date: isoDate,
          toDate: isoToDate,
        },
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/clients/multipleLocations-summarydetails`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (data?.client?.appSettings) {
        sessionStorage.setItem(
          "clientSettings",
          JSON.stringify(data.client.appSettings),
        );
      }

      sessionStorage.setItem(
        "selectedLocation",
        JSON.stringify(selectedLocation._id),
      );
      sessionStorage.setItem("locations", JSON.stringify(selectedLocationIDs));

      const summary = data?.result?.summary || data?.result || [];
      const locationWise = data?.result?.locationWise || [];

      if (onDataSubmit && selectedAllLocation.length > 0) {
        onDataSubmit({
          summary,
          locationWise,
          selectedLocationDet,
          selectedAllLocation,
        });
      }

      onClose();
    } catch (error) {
      setApiError("Can't Get Data From this location !");
      toast.error("Somthing Went Wrong !", { style: toastStyle });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem("selectedAllLocation");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedAllLocation(parsed);
        }
      } catch (e) {
        console.error("Error parsing selectedAllLocation:", e);
      }
    }
  }, [date]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 250, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg space-y-4 rounded-xl bg-white p-6 shadow-2xl"
          >
            <h2 className="font-QSemiBold whitespace-nowrap text-2xl font-semibold text-gray-800">
              Select a Location
            </h2>

            <div className="max-h-96 overflow-y-auto pr-2">
              {isLoading ? (
                <p className="text-gray-600">Loading locations...</p>
              ) : isError ? (
                <p className="text-red-500">Error: {error.message}</p>
              ) : Array.isArray(locations?.selected_location) &&
                locations.selected_location.length > 0 ? (
                locations.selected_location.map((loc, index) => (
                  <motion.div
                    key={index}
                    className="mb-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="mb-2 flex items-center">
                      <input
                        type="radio"
                        id={`location-${index}`}
                        name="location"
                        value={loc.locationEndPoint._id}
                        // ── pre-tick if this matches the stored selectedLocation
                        checked={
                          selectedLocation?._id === loc.locationEndPoint._id
                        }
                        onChange={() => {
                          setSelectedLocation(loc.locationEndPoint);
                          const allLocationIds = (loc.locationID || []).map(
                            (l) => l.id,
                          );
                          setSelectedLocationIDs(allLocationIds);
                          setSelectedAllLocation(loc.locationID);
                        }}
                        className="mr-2 accent-blue-600"
                      />
                      <label
                        htmlFor={`location-${index}`}
                        className="font-QSemiBold whitespace-nowrap text-gray-800"
                      >
                        {loc.locationEndPoint.endpoint_name}
                      </label>
                    </div>

                    {selectedLocation?._id === loc.locationEndPoint._id &&
                      Array.isArray(loc.locationID) &&
                      loc.locationID.length > 0 && (
                        <div className="ml-6 space-y-2">
                          {loc.locationID.map((location, i) => (
                            <motion.div
                              key={i}
                              className="flex items-center"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.03 }}
                            >
                              <input
                                type="checkbox"
                                id={`locid-${location.id}`}
                                value={location.id}
                                // ── pre-tick if this sub-id is in stored locations
                                checked={selectedLocationIDs.includes(
                                  location.id,
                                )}
                                onChange={() =>
                                  handleCheckboxChange(location.id)
                                }
                                className="mr-2 accent-green-600"
                              />
                              <label
                                htmlFor={`locid-${location.id}`}
                                className="text-gray-600"
                              >
                                {location.name}
                              </label>
                            </motion.div>
                          ))}
                        </div>
                      )}
                  </motion.div>
                ))
              ) : (
                <p className="font-QSemiBold whitespace-nowrap text-gray-800">
                  No locations found.
                </p>
              )}
            </div>

            {apiError && (
              <div className="w-full max-w-[100px] text-center sm:text-right">
                <p
                  className="font-QSemiBold overflow-hidden text-ellipsis whitespace-nowrap text-xs text-red-600"
                  title={apiError}
                >
                  {apiError}
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className={`rounded-md border px-4 py-2 transition ${
                  isSubmitting
                    ? "cursor-not-allowed bg-gray-100 text-gray-400"
                    : "border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex items-center justify-center rounded-md px-4 py-2 transition ${
                  isSubmitting
                    ? "cursor-not-allowed bg-blue-300 text-white"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <svg
                    className="h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MultipleLocationModal;